/**
 * IconBadge — disco com ícone tinto pelo token de tema.
 *
 * Uso: rótulo de seção, header de modal/drawer, leading icon de cards.
 * Antes do componente: cada lugar redeclarava
 *   <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-primary)]/15">
 *     <Icon className="h-4 w-4 text-[var(--color-primary)]" />
 *   </span>
 * em ~10 arquivos. Agora é uma linha:
 *   <IconBadge icon={Pencil} variant="primary" size="md" />
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'accent' | 'destructive' | 'warning' | 'muted';
type Size = 'sm' | 'md' | 'lg';

interface IconBadgeProps {
  icon: React.ElementType;
  variant?: Variant;
  size?: Size;
  className?: string;
}

const sizeClass: Record<Size, { box: string; icon: string }> = {
  sm: { box: 'h-7 w-7 rounded-md', icon: 'h-3.5 w-3.5' },
  md: { box: 'h-9 w-9 rounded-lg', icon: 'h-4 w-4' },
  lg: { box: 'h-10 w-10 rounded-lg', icon: 'h-5 w-5' },
};

const variantClass: Record<Variant, { bg: string; text: string }> = {
  primary: { bg: 'bg-[var(--color-primary)]/15', text: 'text-[var(--color-primary)]' },
  accent: { bg: 'bg-[var(--color-accent)]/20', text: 'text-[var(--color-accent)]' },
  destructive: { bg: 'bg-[var(--color-destructive)]/15', text: 'text-[var(--color-destructive)]' },
  warning: { bg: 'bg-[var(--color-warning)]/15', text: 'text-[var(--color-warning)]' },
  muted: { bg: 'bg-[var(--color-surface-elevated)]', text: 'text-[var(--color-text-muted)]' },
};

export function IconBadge({ icon: Icon, variant = 'primary', size = 'md', className }: IconBadgeProps) {
  const s = sizeClass[size];
  const v = variantClass[variant];
  return (
    <span className={cn('inline-flex items-center justify-center shrink-0', s.box, v.bg, className)}>
      <Icon className={cn(s.icon, v.text)} />
    </span>
  );
}
